import {Component, ElementRef, HostBinding, HostListener, OnInit, ViewChild, OnDestroy, Output, EventEmitter} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { User } from '../../models';
import { PatientService, UserService, SocketService, ModalService, CaregiverService, NotificationService } from '../../services';
import { ChatService } from '../../services/chat.service';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import { environment } from '../../../../environments/environment';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import {Subscription} from 'rxjs/Subscription';
import {SidebarService} from '../../services/sidebar.service';
import {TutorialService} from '../../services/tutorial.service';
import {BreadcrumbService, BreadcrumbItem} from '../../services/breadcrumb.service';
import {ListPresentationService} from '../../services/list-presentation.service';

@Component({
  selector: 'app-dashboard-header',
  templateUrl: './dashboard-header.component.html',
  styleUrls: ['./dashboard-header.component.scss']
})
export class DashboardHeaderComponent implements OnInit, OnDestroy {

  @Output() sidebarToggle = new EventEmitter<void>();

  @HostBinding('class.sidebar-collapsed') public isCollapsed = false;
  public isDarkMode = false;
  public readonly textSizes = [
    { key: 'sm', zoom: 1, px: 12, label: 'HEADER.TEXT_SIZE_S' },
    { key: 'md', zoom: 1.15, px: 15, label: 'HEADER.TEXT_SIZE_M' },
    { key: 'lg', zoom: 1.3, px: 18, label: 'HEADER.TEXT_SIZE_L' },
  ];
  public textSize = 'sm';
  public breadcrumbs: BreadcrumbItem[] = [];

  // Active caregivers bar
  allCaregivers: any[] = [];
  activeUserIds: number[] = [];
  apiBaseUrl = environment.api_url.replace(/\/api$/, '');
  isManager = false;
  showHeaderCgDropdown = false;
  headerCgSearch = '';
  private destroy$ = new Subject<void>();

  constructor(
    private userService: UserService,
    private patientService: PatientService,
    public router: Router,
    public translate: TranslateService,
    private socketService: SocketService,
    private modalService: ModalService,
    private caregiverService: CaregiverService,
    private notificationService: NotificationService,
    private activatedRoute: ActivatedRoute,
    private sidebarService: SidebarService,
    private tutorialService: TutorialService,
    public breadcrumbService: BreadcrumbService,
    private elementRef: ElementRef,
    private chatService: ChatService,
    private presentation: ListPresentationService
  ) {}

  /**
   * Trwa pierwsze pobranie opiekunów do paska w nagłówku. Bez tej flagi pusta tablica
   * w trakcie pobierania jest nie do odróżnienia od „ten manager nie ma opiekunów",
   * a `*ngIf` na pasku sprawiał, że nagłówek po prostu nie miał tam nic — bez zapowiedzi,
   * że coś się pojawi.
   */
  public caregiversLoading = false;

  /**
   * Czy w miejscu awatarów rysować szkielet. Pyta `ListPresentationService`, tak jak
   * reszta aplikacji — technika ładowania ma zostać jedną decyzją na cały projekt,
   * także dla widoku, który buduje swój szkielet z prymitywów zamiast z `ListStateComponent`.
   */
  get showsCaregiverSkeleton(): boolean {
    return this.caregiversLoading && this.presentation.loadingStyle === 'skeleton';
  }

  /** Ile kółek narysować — tyle, ile pasek pokazuje awatarów, zanim przejdzie w licznik. */
  readonly caregiverPlaceholders = [0, 1, 2, 3, 4];

  // Który opiekun ma otwarte mini-menu (Profil / Czat) po kliknięciu awatara na górze.
  public cgMenuFor: any = null;

  openCgMenu(cg: any, event: Event): void {
    event.stopPropagation();
    // stopPropagation() nie dopuszcza tu onDocumentClick, więc listę wyszukiwania (+N)
    // trzeba zamknąć ręcznie — inaczej zostawałaby otwarta pod mini-menu.
    this.showHeaderCgDropdown = false;
    this.cgMenuFor = (this.cgMenuFor && this.cgMenuFor.id === cg.id) ? null : cg;
  }

  closeCgMenu(): void {
    this.cgMenuFor = null;
  }

  openCgProfile(cg: any): void {
    this.cgMenuFor = null;
    this.showHeaderCgDropdown = false;
    this.goToCaregiverFromHeader(cg.id);
  }

  openCgChat(cg: any): void {
    this.cgMenuFor = null;
    this.showHeaderCgDropdown = false;
    this.chatService.openChat({
      id: cg.id,
      name: `${cg.first_name || ''} ${cg.last_name || ''}`.trim(),
      avatar: cg.avatar ? this.apiBaseUrl + cg.avatar : null,
      online: this.isCaregiverOnline(cg),
    });
  }

  onToggleClick() {
    this.sidebarService.setCollapsed(!this.isCollapsed);
  }

  ngOnInit() {
    const savedTheme = localStorage.getItem('theme');
    this.isDarkMode = savedTheme === 'dark';
    this.applyTheme();
    try { this.textSize = localStorage.getItem('textSize') || 'sm'; } catch (e) {}
    this.applyTextSize();

    this.sidebarService.collapsed$.subscribe(isCollapsed => {
      this.isCollapsed = isCollapsed;
    });

    this.breadcrumbService.breadcrumbs$.subscribe(items => {
      this.breadcrumbs = items;
    });

    // Active caregivers bar - only for manager
    const role = this.userService.getRole();
    this.isManager = role === 3;
    if (this.isManager) {
      this.caregiversLoading = true;
      this.caregiverService.findAllCaregivers().pipe(takeUntil(this.destroy$)).subscribe(caregivers => {
        this.caregiversLoading = false;
        this.allCaregivers = caregivers.filter(cg => (cg?.type || '').toLowerCase() !== 'observer');
      }, () => {
        // Bez tego pasek zostawałby na szkielecie po nieudanym pobraniu — w nieskończoność.
        this.caregiversLoading = false;
      });

      this.socketService.onStatus().pipe(takeUntil(this.destroy$)).subscribe(activeIds => {
        this.activeUserIds = activeIds || [];
      });
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    // Zamknij mini-menu Profil/Czat po kliknięciu poza awatarem.
    if (this.cgMenuFor) {
      const menuTarget = event.target as HTMLElement;
      if (!menuTarget.closest('.header-cg-avatar') && !menuTarget.closest('.cg-action-menu')) {
        this.cgMenuFor = null;
      }
    }

    if (!this.showHeaderCgDropdown) return;

    const target = event.target as HTMLElement;
    const dropdown = this.elementRef.nativeElement.querySelector('.header-cg-dropdown');
    const trigger = this.elementRef.nativeElement.querySelector('.header-cg-count');

    if (dropdown && !dropdown.contains(target) && trigger && !trigger.contains(target)) {
      this.showHeaderCgDropdown = false;
    }
  }

  isCaregiverOnline(cg: any): boolean {
    return this.activeUserIds.includes(cg.id);
  }

  getOnlineCaregivers(): any[] {
    return this.allCaregivers.filter(cg => this.activeUserIds.includes(cg.id));
  }

  getOfflineCaregivers(): any[] {
    return this.allCaregivers.filter(cg => !this.activeUserIds.includes(cg.id));
  }

  goToCaregiverFromHeader(id: number) {
    this.router.navigate(['/caregivers/caregiver-c', id]);
  }

  getFilteredHeaderCaregivers(): any[] {
    const q = this.headerCgSearch.toLowerCase();
    const list = q
      ? this.allCaregivers.filter(cg =>
        (cg.first_name + ' ' + cg.last_name).toLowerCase().includes(q))
      : this.allCaregivers;
    // Online first
    return list.sort((a, b) => {
      const aOnline = this.activeUserIds.includes(a.id) ? 0 : 1;
      const bOnline = this.activeUserIds.includes(b.id) ? 0 : 1;
      return aOnline - bOnline;
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleTheme() {
    this.isDarkMode = !this.isDarkMode;
    this.applyTheme();
    localStorage.setItem('theme', this.isDarkMode ? 'dark' : 'light');
  }

  openTutorial(): void {
    const pageModules = this.tutorialService.getPageModules();
    if (pageModules.length > 0) {
      this.tutorialService.resetModule(pageModules[0].id);
      this.tutorialService.startModuleById(pageModules[0].id);
    } else {
      this.tutorialService.resetTutorial();
      this.tutorialService.startTutorial(this.userService.getCurrentUser()?.role || 'ROLE_API_MANAGER');
    }
  }

  private applyTheme() {
    if (this.isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  }

  setTextSize(key: string) {
    this.textSize = key;
    this.applyTextSize();
    try { localStorage.setItem('textSize', key); } catch (e) {}
  }

  /** Większy tekst dla osób ze słabszym wzrokiem: app.component powiększa treść strony (zoom) wg --ui-zoom. */
  private applyTextSize() {
    const size = this.textSizes.find(s => s.key === this.textSize) || this.textSizes[0];
    document.body.style.setProperty('--ui-zoom', String(size.zoom));
  }

}
